import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "../../assets/styles/homehero.css";
import { Link } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";

const STATES = {
  INIT: "init",
  BLOCKED: "blocked",
  PLAYING: "playing",
};

export default function HomeHero({ sources, poster, headline, gate = false }) {
  const videoRef = useRef(null);
  const [state, setState] = useState(STATES.INIT);
  const [reducedMotion, setReducedMotion] = useState(false);
  const t = useTranslation();

  /* prefers-reduced-motion */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReducedMotion(e.matches);
    onChange(mq);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", onChange)
        : mq.removeListener(onChange);
    };
  }, []);

  /* Try autoplay ONLY when gate opens */
  useEffect(() => {
    if (!gate || reducedMotion) return;           // <-- wait for gate
    const v = videoRef.current;
    if (!v) return;

    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    v.autoplay = true;

    const onPlaying = () => setState(STATES.PLAYING);
    const onTimeUpdate = () => {
      if (!v.paused && v.currentTime > 0) setState(STATES.PLAYING);
    };
    const onError = (e) => console.warn("video error", v.error || e);

    v.addEventListener("playing", onPlaying);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("error", onError);

    v.load();
    v.play().catch(() => setState(STATES.BLOCKED));

    return () => {
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("error", onError);
    };
  }, [gate, reducedMotion]);

  const handlePlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play()
      .then(() => setState(STATES.PLAYING))
      .catch(() => v.setAttribute("controls", "controls"));
  };

  const showPoster = reducedMotion || state !== STATES.PLAYING;
  const showButton = state === STATES.BLOCKED && !reducedMotion;

  return (
    <header className="homeHero">
      {/* Poster lives under video; visible until PLAYING */}
      <img
        src={poster}
        alt=""
        aria-hidden="true"
        className="homeHeroVideo"
        style={{ opacity: showPoster ? 1 : 0 }}
      />

      {!reducedMotion && gate && (
        <video
          ref={videoRef}
          className={`homeHeroVideo transition-opacity duration-700 ${
            state === STATES.PLAYING ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
          loop
          muted
          playsInline
          preload={gate ? "auto" : "none"}
          aria-hidden="true"
          disablePictureInPicture
          webkit-playsinline="true"
        >
          {/* MP4 first for Safari */}
          {sources?.mp4 && <source src={sources.mp4} type="video/mp4" />}
          {sources?.webm && <source src={sources.webm} type="video/webm" />}
        </video>
      )}

      {showButton && (
        <button
          type="button"
          className="homeHeroPlayBtn"
          onClick={handlePlayClick}
          aria-label="Play background video"
        >
          <span className="sr-only">Play</span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
            className="playbuttonicon"
          >
            <path d="M6 4l12 8-12 8V4z" fill="currentColor" />
          </svg>
        </button>
      )}

      <div className="homeHeroContent">
        <h2 className="homeHeroHeadline">{headline}</h2>
        <Link to="/szolgaltatasok" className="homeHeroCta">
          {t("home.hero.newcomer")}
        </Link>
      </div>

      <div className="homeHeroMask" />
    </header>
  );
}

HomeHero.propTypes = {
  sources: PropTypes.shape({
    webm: PropTypes.string,
    mp4: PropTypes.string.isRequired,
  }).isRequired,
  poster: PropTypes.string,
  headline: PropTypes.string.isRequired,
  gate: PropTypes.bool,
};
